import type { ReactNode } from 'react';
import type { Semaforo } from '@/api/painel';
import { deltaPct } from './format';

const pct1 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1, minimumFractionDigits: 1 });

/** Tons de texto pra variação (contraste ≥ 4,5:1 no branco) — sempre com seta. */
const UP = '#007A4D';
const DOWN = '#C81E33';

/**
 * Variação contra o período anterior (padrão da Tupi): seta + % + "vs. anterior".
 * Sem base (org sem acesso naquela janela, ou anterior zero) → traço neutro.
 * Base parcial (o acesso cobre só parte da janela anterior) → também sem %: o
 * investidor novo veria "+100%" no 1º mês só porque a base tem metade do tempo.
 */
export function Delta({
  atual,
  anterior,
  parcial = false
}: {
  atual: number;
  anterior: number | null | undefined;
  parcial?: boolean;
}) {
  if (parcial && anterior != null) {
    return (
      <span className="text-[11.5px] font-medium text-bow-royal/45">
        — base parcial no período anterior
      </span>
    );
  }
  const pct = deltaPct(atual, anterior);
  if (pct == null) {
    return <span className="text-[11.5px] font-medium text-bow-royal/45">— sem base no período anterior</span>;
  }
  const estavel = Math.abs(pct) < 0.05;
  const up = pct > 0;
  return (
    <span
      className="inline-flex items-center gap-1 text-[11.5px] font-semibold"
      style={{ color: estavel ? undefined : up ? UP : DOWN }}
    >
      <span aria-hidden>{estavel ? '=' : up ? '▲' : '▼'}</span>
      <span className={estavel ? 'text-bow-royal/55' : undefined}>
        {estavel ? 'estável' : `${up ? '+' : '−'}${pct1.format(Math.abs(pct))}%`}
      </span>
      <span className="font-medium text-bow-royal/45">vs. anterior</span>
    </span>
  );
}

/** Stat tile: rótulo · valor (proporcional, grande) · variação · nota opcional. */
export function Kpi({
  label,
  value,
  unit,
  note,
  delta,
  hero = false
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  note?: ReactNode;
  /** Valor atual e do período anterior (o mesmo número, cru) pra calcular a %. */
  delta?: { atual: number; anterior: number | null | undefined; parcial?: boolean };
  hero?: boolean;
}) {
  return (
    <div className="rounded-3xl bg-white px-4 py-4 shadow-soft">
      <div className="text-[12px] font-medium text-bow-royal/55">{label}</div>
      <div
        className={`mt-1.5 font-semibold leading-none tracking-[-0.03em] text-bow-royal ${
          hero ? 'text-[34px] lg:text-[44px]' : 'text-[24px]'
        }`}
      >
        {value}
        {unit && <span className="ml-1 text-[13px] font-medium text-bow-royal/45">{unit}</span>}
      </div>
      {delta && (
        <div className="mt-2">
          <Delta atual={delta.atual} anterior={delta.anterior} parcial={delta.parcial} />
        </div>
      )}
      {note && <div className="mt-2 text-[11.5px] leading-snug text-bow-royal/50">{note}</div>}
    </div>
  );
}

const SEMAFORO: Record<Semaforo, { color: string; label: string; icon: string }> = {
  verde: { color: '#00C072', label: 'Funcionando', icon: '●' },
  amarelo: { color: '#FFB92A', label: 'Atenção', icon: '▲' },
  vermelho: { color: '#EE263E', label: 'Fora do ar', icon: '■' }
};

/** Semáforo de status — cor + forma + texto (nunca só a cor). */
export function StatusLight({ value, detail }: { value: Semaforo; detail?: string }) {
  const s = SEMAFORO[value];
  return (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-bow-royal">
      <span aria-hidden style={{ color: s.color, fontSize: 11 }}>
        {s.icon}
      </span>
      {s.label}
      {detail && <span className="font-medium text-bow-royal/50">· {detail}</span>}
    </span>
  );
}

export function EstimatedTag() {
  return (
    <span className="ml-1 rounded-full bg-bow-cream-2 px-1.5 py-[1px] text-[10.5px] font-semibold uppercase tracking-[0.04em] text-bow-royal/60">
      estimada
    </span>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="m-0 mb-2 mt-7 text-[12px] font-semibold uppercase tracking-[0.06em] text-bow-royal/55">
      {children}
    </h2>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="mt-4 rounded-2xl bg-white p-4 text-[14px] text-bow-royal">
      {message}
      {onRetry && (
        <button type="button" onClick={onRetry} className="ml-2 font-semibold text-bow-blue underline">
          Tentar de novo
        </button>
      )}
    </div>
  );
}
